import React, { useState } from 'react';
import { Card } from './ui/card';
import { Button } from './ui/button';
import { useAuth } from '../AuthContext';
import { X, PlayCircle, Heart, MessagesSquare } from 'lucide-react';
import { Link } from 'react-router-dom';

// Trainer Welcome Video — shown to clients at the top of the dashboard.
// V1 uses the existing JFK9 site video. Switch the src URL when a trainer
// reel is uploaded to Object Storage. Facebook Reels cannot be embedded
// directly, so we link out for that source while displaying the local
// welcome video inline.
const FB_REEL_URL = 'https://www.facebook.com/reel/1550653313146040';
const INLINE_VIDEO_URL = 'https://www.jointforcesk9.com/logo_video_site.webm';
const DISMISS_KEY = 'jfk9_welcome_video_dismissed_v1';

export const WelcomeVideoBanner = () => {
  const { user } = useAuth();
  const [open, setOpen] = useState(() => !localStorage.getItem(DISMISS_KEY));
  if (!open || !user || user.role !== 'CLIENT') return null;

  const dismiss = () => {
    localStorage.setItem(DISMISS_KEY, '1');
    setOpen(false);
  };

  return (
    <Card className='relative overflow-hidden border-2 border-[#1B3A87]/30 dark:border-[#93C5FD]/30 bg-white dark:bg-slate-900 mb-6' data-testid='welcome-video-banner'>
      <button
        onClick={dismiss}
        className='absolute top-3 right-3 z-10 bg-white/85 dark:bg-slate-800/85 rounded-full p-1 text-slate-500 hover:text-[#B42318]'
        aria-label='Dismiss welcome video'
        data-testid='welcome-video-dismiss'
      >
        <X className='w-4 h-4' />
      </button>
      <div className='grid md:grid-cols-[1.2fr_1fr] gap-0'>
        <div className='relative aspect-video md:aspect-auto bg-black'>
          <video
            src={INLINE_VIDEO_URL}
            controls
            preload='metadata'
            playsInline
            className='w-full h-full object-contain bg-black'
            data-testid='welcome-video-player'
          />
        </div>
        <div className='p-5 sm:p-6 flex flex-col justify-center'>
          <div className='text-[10px] uppercase tracking-[0.18em] font-bold text-[#C8102E]'>Welcome to JFK9</div>
          <h2 className='font-display text-xl sm:text-2xl font-bold text-[#0B1220] dark:text-slate-50 mt-1.5 leading-tight'>
            Welcome to Joint Forces K9 — the pack just got bigger.
          </h2>
          <p className='text-sm text-slate-600 dark:text-slate-300 mt-2 leading-relaxed'>
            Our trainers put together a short welcome message for you. Stay
            connected by joining the community board and chat — that is where
            JFK9 news, success timelines, and member-only updates live.
          </p>
          <div className='mt-4 flex flex-wrap gap-2'>
            <a
              href={FB_REEL_URL}
              target='_blank'
              rel='noopener noreferrer'
              className='inline-flex items-center gap-1.5 px-3 py-2 rounded-md text-xs font-semibold bg-[#1B3A87] hover:bg-[#16306F] text-white'
              data-testid='welcome-fb-link'
            >
              <PlayCircle className='w-4 h-4' /> Watch trainer welcome on Facebook
            </a>
            <Link
              to='/community'
              className='inline-flex items-center gap-1.5 px-3 py-2 rounded-md text-xs font-semibold border border-[#C8102E]/40 text-[#C8102E] hover:bg-[#C8102E]/10'
              data-testid='welcome-community-link'
            >
              <MessagesSquare className='w-4 h-4' /> Join the Community Board
            </Link>
            <Button
              variant='ghost'
              size='sm'
              onClick={dismiss}
              className='text-slate-500 dark:text-slate-300'
              data-testid='welcome-dismiss-button'
            >
              <Heart className='w-4 h-4 mr-1.5' /> Got it
            </Button>
          </div>
        </div>
      </div>
    </Card>
  );
};

export default WelcomeVideoBanner;
